import React from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '../../../shared/components/ui/Card';

/**
 * Read-only admin dashboard snapshot (same data shape as GET /admin/merchants/:id/dashboard).
 * @param {Object} props
 * @param {{ loading: boolean, error: string|null, data: object|null, filter: string }} props.previewState
 * @param {(filter: string) => void} props.onFilterChange
 */
export default function MerchantDashboardPreviewPanel({ previewState, onFilterChange }) {
    const { loading, error, data, filter } = previewState;

    return (
        <div className="space-y-4">
            <p className="text-xs text-slate-500">
                Read-only snapshot of what this merchant sees on Dashboard Overview (same metrics as their app; time
                range matches their filters).
            </p>
            {loading && !data && <p className="text-sm text-slate-500 py-4">Loading dashboard…</p>}
            {error && !data && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {error}
                </p>
            )}
            {data?.analytics && (
                <>
                    <div className="flex flex-wrap gap-2">
                        {['total', 'year', 'month', 'week', 'day'].map((option) => (
                            <button
                                key={option}
                                type="button"
                                disabled={loading}
                                onClick={() => {
                                    if (option === filter) {
                                        return;
                                    }
                                    onFilterChange(option);
                                }}
                                className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                                    filter === option
                                        ? 'bg-primary text-white border-primary'
                                        : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                } disabled:opacity-50`}
                            >
                                {option.charAt(0).toUpperCase() + option.slice(1)}
                            </button>
                        ))}
                    </div>
                    {loading && data && <p className="text-xs text-slate-500">Updating…</p>}
                    {(() => {
                        const stats = data.analytics.stats || {};
                        const f = filter;
                        const cards =
                            f === 'total'
                                ? [
                                      { label: 'Total Customers', value: stats.totalCustomers },
                                      { label: 'Reward Redemptions', value: stats.redeemed },
                                      { label: 'Points Awarded', value: stats.pointsAwarded },
                                      { label: 'Visits Total', value: stats.visits },
                                      { label: 'Avg Spend Per Visit ($)', value: stats.averageSpend },
                                  ]
                                : [
                                      { label: 'New Customers', value: stats.newCustomers },
                                      {
                                          label: `Scans ${f.charAt(0).toUpperCase() + f.slice(1)}`,
                                          value: stats.scans,
                                      },
                                      {
                                          label: `Redeemed ${f.charAt(0).toUpperCase() + f.slice(1)}`,
                                          value: stats.redeemed,
                                      },
                                      { label: 'Points Awarded', value: stats.pointsAwarded },
                                      {
                                          label: 'Money Earned ($)',
                                          value:
                                              typeof stats.moneyEarned === 'number'
                                                  ? stats.moneyEarned.toFixed(2)
                                                  : stats.moneyEarned,
                                      },
                                      {
                                          label: `Visits ${f.charAt(0).toUpperCase() + f.slice(1)}`,
                                          value: stats.visits,
                                      },
                                      {
                                          label: `Avg Spend Per Visit ${f.charAt(0).toUpperCase() + f.slice(1)} ($)`,
                                          value: stats.averageSpend,
                                      },
                                  ];
                        const topP = data.analytics.topCustomersByPoints || [];
                        const topR = data.analytics.topRewards || [];
                        const logs = data.activity?.logs || [];
                        const hasNumbers = cards.some((c) => {
                            const v = c.value;
                            if (typeof v === 'number') {
                                return v > 0;
                            }
                            if (typeof v === 'string') {
                                return parseFloat(v) > 0;
                            }
                            return false;
                        });
                        const hasLists = topP.length > 0 || topR.length > 0;
                        const hasLogs = logs.length > 0;
                        if (!hasNumbers && !hasLists && !hasLogs) {
                            return (
                                <p className="text-sm text-slate-500 py-2">
                                    No dashboard activity yet for this merchant.
                                </p>
                            );
                        }
                        return (
                            <div className="space-y-4">
                                {hasNumbers && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {cards.map((card, idx) => (
                                            <Card key={idx} className="shadow-sm">
                                                <CardContent className="p-3">
                                                    <p className="text-xs text-slate-500 line-clamp-2">{card.label}</p>
                                                    <p
                                                        className="text-base font-semibold text-primary mt-1 truncate"
                                                        title={String(card.value)}
                                                    >
                                                        {card.value}
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="rounded-lg border border-slate-200 p-3">
                                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                            Top customers (points)
                                        </h4>
                                        {topP.length === 0 ? (
                                            <p className="text-xs text-slate-500">None yet.</p>
                                        ) : (
                                            <ul className="divide-y divide-slate-100">
                                                {topP.slice(0, 5).map((c, i) => (
                                                    <li
                                                        key={i}
                                                        className="py-1.5 flex justify-between text-xs gap-2"
                                                    >
                                                        <span className="truncate">{c.name}</span>
                                                        <span className="text-primary font-medium shrink-0">
                                                            {c.points} pts
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="rounded-lg border border-slate-200 p-3">
                                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                            Most redeemed rewards
                                        </h4>
                                        {topR.length === 0 ? (
                                            <p className="text-xs text-slate-500">None in this range.</p>
                                        ) : (
                                            <ul className="divide-y divide-slate-100">
                                                {topR.slice(0, 5).map((r, i) => (
                                                    <li
                                                        key={i}
                                                        className="py-1.5 flex justify-between text-xs gap-2"
                                                    >
                                                        <span className="truncate">{r.title}</span>
                                                        <span className="text-primary font-medium shrink-0">
                                                            {r.redemptions}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                {hasLogs && (
                                    <div className="rounded-lg border border-slate-200 p-3">
                                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                            Recent activity
                                        </h4>
                                        <ul className="space-y-2 text-xs text-slate-700">
                                            {logs.map((log, i) => (
                                                <li key={i} className="flex gap-2">
                                                    <span className="shrink-0" aria-hidden>
                                                        {log.icon || '•'}
                                                    </span>
                                                    <span className="min-w-0 flex-1">{log.message}</span>
                                                    <span className="shrink-0 text-slate-400 whitespace-nowrap">
                                                        {log.timestamp
                                                            ? format(new Date(log.timestamp), 'MMM d, h:mm a')
                                                            : ''}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
}
